import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { SectionHeader } from "@/components/site/Reveal";
import { CONTACT } from "@/data";
import { openWhatsApp } from "@/utils/whatsapp";

const initial = { name: "", phone: "", email: "", service: "", details: "" };

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t.contact.form.name + " / " + t.contact.form.phone);
      return;
    }
    const m = t.whatsappMsg;
    const lines = [
      m.intro,
      "",
      `${m.name}: ${form.name}`,
      `${m.phone}: ${form.phone}`,
      form.email ? `${m.email}: ${form.email}` : null,
      form.service ? `${m.service}: ${form.service}` : null,
      form.details ? `${m.details}: ${form.details}` : null,
    ].filter(Boolean);
    openWhatsApp(lines.join("\n"));
    toast.success("WhatsApp");
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-[#111827] relative noise-overlay" data-testid="contact-section">
      <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <SectionHeader tag={t.contact.tag} title={t.contact.title} subtitle={t.contact.subtitle} />

          <div className="mt-10 space-y-4">
            {CONTACT.phones.map((p, i) => (
              <div key={i} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#1C2539] p-5">
                <a
                  data-testid={`contact-phone-${i}`}
                  href={`tel:${p.tel}`}
                  className="flex items-center gap-3 font-display font-bold text-lg sm:text-xl hover:text-[#FF8C42] transition-colors"
                >
                  <span className="grid place-items-center h-11 w-11 rounded-full bg-[#E85D04]/15 text-[#FF8C42]">
                    <Phone className="h-5 w-5" />
                  </span>
                  {p.display}
                </a>
                <a
                  data-testid={`contact-whatsapp-${i}`}
                  href={`https://wa.me/${p.tel.replace("+", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#20bd5a] px-4 py-2.5 text-sm font-bold text-white transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            ))}
          </div>
        </div>

        <motion.form
          data-testid="contact-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="rounded-3xl border border-white/10 bg-[#0B132B] p-7 sm:p-9"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#C7CCD6]">{t.contact.form.name} *</Label>
              <Input id="name" data-testid="form-name" value={form.name} onChange={update("name")}
                className="bg-[#1C2539] border-white/10 h-12 focus-visible:ring-[#E85D04]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#C7CCD6]">{t.contact.form.phone} *</Label>
              <Input id="phone" data-testid="form-phone" value={form.phone} onChange={update("phone")}
                className="bg-[#1C2539] border-white/10 h-12 focus-visible:ring-[#E85D04]" />
            </div>
          </div>

          <div className="space-y-2 mt-5">
            <Label htmlFor="email" className="text-[#C7CCD6]">{t.contact.form.email}</Label>
            <Input id="email" type="email" data-testid="form-email" value={form.email} onChange={update("email")}
              className="bg-[#1C2539] border-white/10 h-12 focus-visible:ring-[#E85D04]" />
          </div>

          <div className="space-y-2 mt-5">
            <Label className="text-[#C7CCD6]">{t.contact.form.service}</Label>
            <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
              <SelectTrigger data-testid="form-service" className="bg-[#1C2539] border-white/10 h-12 focus:ring-[#E85D04]">
                <SelectValue placeholder={t.contact.form.servicePlaceholder} />
              </SelectTrigger>
              <SelectContent className="bg-[#1C2539] border-white/10 text-white max-h-72">
                {t.services.items.map((s) => (
                  <SelectItem key={s.key} value={s.title} className="focus:bg-white/10 focus:text-[#FF8C42]">
                    {s.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 mt-5">
            <Label htmlFor="details" className="text-[#C7CCD6]">{t.contact.form.details}</Label>
            <Textarea id="details" data-testid="form-details" rows={4} value={form.details} onChange={update("details")}
              placeholder={t.contact.form.detailsPlaceholder}
              className="bg-[#1C2539] border-white/10 focus-visible:ring-[#E85D04] resize-none" />
          </div>

          <button
            type="submit"
            data-testid="form-submit"
            className="mt-7 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#E85D04] hover:bg-[#D05303] px-6 py-4 font-bold text-white transition-colors"
          >
            <Send className="h-5 w-5" />
            {t.contact.form.submit}
          </button>
          <p className="text-xs text-[#6B7280] text-center mt-4">{t.contact.form.note}</p>
        </motion.form>
      </div>
    </section>
  );
};
